import React, { useEffect, useState } from 'react';
import { Grid3x3, PencilRuler, Palette, Home, Bookmark, Eye } from 'lucide-react';
import LoopVideo, { filmSrc } from './elements/loopVideo.jsx';
import ReelViewer from './elements/reelViewer.jsx';
import { getSaved, subscribeSaved } from './elements/savedStore.jsx';
import { shortViews } from './elements/listingFormat.jsx';
import { listSentShares } from './elements/sharesApi.jsx';
import ProfileMenu from './elements/profileMenu.jsx';
import EditProfileModal from './elements/editProfileModal.jsx';
import { useMyDesigns, DesignsGrid } from './elements/myDesigns.jsx';
import PlanDraftsPage from './planDrafts.jsx';
import './profileUser.scss';

const MOCK_USER = {
  username: 'sara_ahmadi',
  name: 'سارا احمدی',
  avatarInitial: 'س',
  stats: { views: 452 },
};

// onOpenRequests: «درخواست‌های من» → تبِ + (فهرستِ درخواست‌ها و «ثبت درخواست جدید»)
function ProfileUserPage({ user, onLogout, onUserUpdate, onOpenRequests }) {
  const [editOpen, setEditOpen] = useState(false);
  // نام و حرفِ اولِ عکس از حسابِ واقعی؛ بقیه فعلاً نمونه است
  const fullName = user ? `${user.firstName} ${user.lastName}` : MOCK_USER.name;
  const initial = user ? user.firstName.charAt(0) : MOCK_USER.avatarInitial;
  const [activeTab, setActiveTab] = useState('grid');
  // «خانه‌ی رویایی من»: طراحیِ دوبعدی → سه‌بعدی → انتشار (مثلِ مشاور، بدونِ آگهی)
  const [dreamOpen, setDreamOpen] = useState(false);
  // تبِ اول: فایل‌هایی که کاربر «ذخیره» کرده (خودکار، با هر ذخیره/برداشتن به‌روز می‌شود)
  const [saved, setSaved] = useState(getSaved);
  useEffect(() => subscribeSaved(() => setSaved(getSaved())), []);
  const [openSaved, setOpenSaved] = useState(null);
  // طرح‌های ذخیره‌شده‌ی خودِ کاربر — در «فایل‌ها» و «خانه‌های طراحی‌شده»
  const designs = useMyDesigns(user);
  // اشتراک‌هایی که کاربر فرستاده: تعدادشان «اشتراک‌گذاری‌شده»، و هر نفری که لینکِ اشتراکی را باز کرده +۱ «بازدید»
  const [shares, setShares] = useState({ count: 0, visitors: 0 });
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    listSentShares()
      .then((r) => {
        if (!alive) return;
        const items = r.items || [];
        setShares({ count: items.length, visitors: items.reduce((s, x) => s + (Number(x.visitorCount) || 0), 0) });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);
  const totalViews = designs.items.reduce((s, d) => s + (d.views || 0), 0) + shares.visitors;
  const stats = [
    { label: 'بازدید', value: user ? totalViews : 0 },
    { label: 'طراحی‌شده', value: user ? designs.items.length : 0 },
    { label: 'اشتراک‌گذاری‌شده', value: user ? shares.count : 0 },
  ];

  return (
    <div className="profile-user-page">
      <header className="profile-user-page__header">
        <span className="profile-user-page__username">{fullName}</span>
        <ProfileMenu onLogout={onLogout} onEdit={user ? () => setEditOpen(true) : undefined} />
      </header>

<section className="profile-user-page__top">
  <div className="profile-user-page__avatar">
    <span>{initial}</span>
  </div>

  <div className="profile-user-page__info">
    <strong>{fullName}</strong>
    {user?.bio && <p className="profile-user-page__bio">{user.bio}</p>}
    <div className="profile-user-page__stats">
      {stats.map((s) => (
        <div key={s.label} className="profile-user-page__stat">
          <strong>{s.value.toLocaleString('fa-IR')}</strong>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  </div>
</section>

<section className="profile-user-page__actions">
  <button type="button" className="profile-user-page__btn profile-user-page__btn--primary" onClick={() => setDreamOpen(true)}>
    <Home size={16} strokeWidth={2.2} />
    <span>خانه‌ی رویایی من</span>
  </button>
  <button type="button" className="profile-user-page__btn" onClick={onOpenRequests}>
    درخواست‌های من
  </button>
</section>

      <nav className="profile-user-page__tabs">
        <button
          type="button"
          className={activeTab === 'grid' ? 'is-active' : ''}
          onClick={() => setActiveTab('grid')}
          aria-label="ذخیره‌شده‌ها"
        >
          <Grid3x3 size={22} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          className={activeTab === 'designed' ? 'is-active' : ''}
          onClick={() => setActiveTab('designed')}
          aria-label="خانه‌های طراحی‌شده"
        >
          <PencilRuler size={22} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          className={activeTab === 'styled' ? 'is-active' : ''}
          onClick={() => setActiveTab('styled')}
          aria-label="خانه‌های دیزاین‌شده"
        >
          <Palette size={22} strokeWidth={1.6} />
        </button>
      </nav>

      {activeTab === 'grid' &&
        (saved.length ? (
          <section className="profile-user-page__grid">
            {saved.map((d, i) => (
              <button key={d.id} type="button" className="profile-user-page__saved" onClick={() => setOpenSaved(i)}>
                {d.filmUrl ? (
                  <LoopVideo src={filmSrc(d, true)} poster={d.thumbUrl} threshold={0.5} />
                ) : d.thumbUrl ? (
                  <img src={d.thumbUrl} alt="" loading="lazy" />
                ) : (
                  <Home size={24} strokeWidth={1.4} />
                )}
                <span className="profile-user-page__saved-views">
                  <Eye size={11} strokeWidth={2.2} />
                  {shortViews(d.views)}
                </span>
              </button>
            ))}
          </section>
        ) : (
          <div className="profile-user-page__empty">
            <Bookmark size={30} strokeWidth={1.3} />
            <p>هنوز فایلی ذخیره نکرده‌اید. در اکسپلور یا ریلز روی «ذخیره» بزنید تا این‌جا بیاید.</p>
          </div>
        ))}
      {activeTab === 'designed' && (
        <DesignsGrid
          className="profile-user-page__grid"
          designs={designs.items}
          loading={designs.loading}
          error={designs.error}
          onDeleted={designs.remove}
          emptyText="هنوز خانه‌ای طراحی و منتشر نکرده‌اید. «خانه‌ی رویایی من» را بزنید."
        />
      )}
      {activeTab === 'styled' && (
        <section className="profile-user-page__grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="profile-user-page__grid-item">
              <Home size={26} strokeWidth={1.4} />
            </div>
          ))}
        </section>
      )}
      {openSaved !== null && saved[openSaved] && (
        <ReelViewer items={saved} startIndex={openSaved} onClose={() => setOpenSaved(null)} />
      )}
      {dreamOpen && (
        <div className="profile-user-page__dream">
          <PlanDraftsPage dream onClose={() => setDreamOpen(false)} />
        </div>
      )}
      {editOpen && (
        <EditProfileModal
          user={user}
          onClose={() => setEditOpen(false)}
          onSaved={(u) => {
            onUserUpdate?.(u);
            setEditOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default ProfileUserPage;